import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { CATEGORIES, PAYMENT_METHODS } from "@/lib/constants";
import { Sparkles, Save, Target, X } from "lucide-react";
import { logger } from "@/lib/logger";

const ORDER_TYPES = [
  { v: "compra", label: "🛒 Compras" },
  { v: "servico", label: "🛠 Serviços" },
  { v: "permuta", label: "🔄 Permutas" },
  { v: "doacao", label: "❤️ Doações" },
];

const CONDITIONS = [
  { v: "novo", label: "Novo", icon: "✨" },
  { v: "usado", label: "Usado", icon: "📦" },
  { v: "ambos", label: "Ambos", icon: "🔁" },
];

const SERVICE_LOCATIONS = [
  { v: "presencial", label: "Presencial" },
  { v: "online", label: "Online" },
  { v: "ambos", label: "Ambos" },
];

export default function Preferences() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [prefs, setPrefs] = useState({
    categories: [],
    keywords: [],
    cities: [],
    max_distance_km: 50,
    min_price: "",
    max_price: "",
    order_types: ["compra", "permuta", "doacao", "servico"],
    brands: [],
    conditions: [],
    payment_methods: [],
    service_locations: [],
  });
  const [kwInput, setKwInput] = useState("");
  const [cityInput, setCityInput] = useState("");
  const [brandInput, setBrandInput] = useState("");

  useEffect(() => {
    api.get("/me/preferences")
      .then((r) => {
        const p = r.data || {};
        setPrefs({
          categories: p.categories || [],
          keywords: p.keywords || [],
          cities: p.cities || [],
          max_distance_km: p.max_distance_km ?? 50,
          min_price: p.min_price ?? "",
          max_price: p.max_price ?? "",
          order_types: p.order_types || ["compra", "permuta", "doacao", "servico"],
          brands: p.brands || [],
          conditions: p.conditions || [],
          payment_methods: p.payment_methods || [],
          service_locations: p.service_locations || [],
        });
      })
      .catch((err) => logger.warn("preferences load", err?.message))
      .finally(() => setLoading(false));
  }, []);

  const toggleArr = (key, val) => {
    setPrefs((p) => {
      const has = p[key].includes(val);
      return { ...p, [key]: has ? p[key].filter((x) => x !== val) : [...p[key], val] };
    });
  };

  const addKeyword = () => {
    const v = kwInput.trim().toLowerCase();
    if (!v || prefs.keywords.includes(v)) return;
    setPrefs((p) => ({ ...p, keywords: [...p.keywords, v] }));
    setKwInput("");
  };

  const addCity = () => {
    const v = cityInput.trim();
    if (!v || prefs.cities.includes(v)) return;
    setPrefs((p) => ({ ...p, cities: [...p.cities, v] }));
    setCityInput("");
  };

  const addBrand = () => {
    const v = brandInput.trim();
    if (!v || prefs.brands.includes(v)) return;
    setPrefs((p) => ({ ...p, brands: [...p.brands, v] }));
    setBrandInput("");
  };

  const removeItem = (key, val) => setPrefs((p) => ({ ...p, [key]: p[key].filter((x) => x !== val) }));

  const save = async () => {
    // Client-side sanity check: min <= max when both set
    if (prefs.min_price !== "" && prefs.max_price !== "" &&
        Number(prefs.min_price) > Number(prefs.max_price)) {
      toast.error("Valor mínimo não pode ser maior que o máximo.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...prefs,
        min_price: prefs.min_price === "" ? null : Number(prefs.min_price),
        max_price: prefs.max_price === "" ? null : Number(prefs.max_price),
        max_distance_km: Number(prefs.max_distance_km) || 50,
      };
      await api.put("/me/preferences", payload);
      toast.success("Especialidades salvas! Agora só recebe o que combina com você.");
      nav("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="max-w-3xl mx-auto px-4 py-10 text-muted-foreground">Carregando...</div>;
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 pb-24">
      <div className="mb-6 flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-primary/15 border border-primary/30 flex items-center justify-center shrink-0">
          <Target size={20} className="text-primary" />
        </div>
        <div>
          <p className="text-xs uppercase tracking-widest text-primary font-bold">Notificações inteligentes</p>
          <h1 className="font-heading font-black text-3xl md:text-4xl tracking-tight leading-tight">
            Minhas Especialidades
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Selecione exatamente as oportunidades que você quer receber. Só isso vai aparecer no seu feed e nas suas notificações.
          </p>
        </div>
      </div>

      <div className="space-y-6">
        {/* Order types */}
        <section className="bg-card border border-border rounded-2xl p-5" data-testid="prefs-order-types">
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">Tipos de oportunidade</Label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
            {ORDER_TYPES.map((t) => {
              const active = prefs.order_types.includes(t.v);
              return (
                <button
                  key={t.v}
                  type="button"
                  data-testid={`pref-type-${t.v}`}
                  onClick={() => toggleArr("order_types", t.v)}
                  className={`h-12 rounded-xl border text-sm font-bold transition-colors ${
                    active
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border hover:border-primary/50"
                  }`}
                >
                  {t.label}
                </button>
              );
            })}
          </div>
        </section>

        {/* Categories */}
        <section className="bg-card border border-border rounded-2xl p-5" data-testid="prefs-categories">
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">Categorias que atendo</Label>
          <p className="text-xs text-muted-foreground mt-1">Deixe vazio para receber todas.</p>
          <div className="flex flex-wrap gap-2 mt-3">
            {CATEGORIES.map((c) => {
              const active = prefs.categories.includes(c);
              return (
                <button
                  key={c}
                  type="button"
                  data-testid={`pref-cat-${c}`}
                  onClick={() => toggleArr("categories", c)}
                  className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                    active
                      ? "bg-primary text-primary-foreground border-primary"
                      : "border-border hover:border-primary/50"
                  }`}
                >
                  {c}
                </button>
              );
            })}
          </div>
        </section>

        {/* Cities */}
        <section className="bg-card border border-border rounded-2xl p-5" data-testid="prefs-cities">
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">Cidades que atendo</Label>
          <div className="flex gap-2 mt-2">
            <Input
              value={cityInput}
              onChange={(e) => setCityInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCity())}
              placeholder="Ex: São Paulo"
              data-testid="pref-city-input"
              className="h-11"
            />
            <Button type="button" onClick={addCity} data-testid="pref-city-add" variant="outline" className="h-11">
              Adicionar
            </Button>
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            {prefs.cities.map((c) => (
              <span key={c} data-testid={`pref-city-${c}`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-sm">
                {c}
                <button type="button" onClick={() => removeItem("cities", c)} className="hover:opacity-70">
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>
        </section>

        {/* Distance and price */}
        <section className="bg-card border border-border rounded-2xl p-5 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="prefs-range">
          <div>
            <Label htmlFor="pref-dist" className="text-xs uppercase tracking-wider text-muted-foreground">Distância máx.</Label>
            <div className="mt-2 flex items-center gap-2">
              <Input
                id="pref-dist"
                data-testid="pref-distance"
                type="number"
                min={1}
                value={prefs.max_distance_km}
                onChange={(e) => setPrefs((p) => ({ ...p, max_distance_km: e.target.value }))}
                className="h-11"
              />
              <span className="text-sm text-muted-foreground">km</span>
            </div>
          </div>
          <div>
            <Label htmlFor="pref-min" className="text-xs uppercase tracking-wider text-muted-foreground">Valor mín. (R$)</Label>
            <Input
              id="pref-min"
              data-testid="pref-min-price"
              type="number"
              min={0}
              value={prefs.min_price}
              onChange={(e) => setPrefs((p) => ({ ...p, min_price: e.target.value }))}
              placeholder="0"
              className="h-11 mt-2"
            />
          </div>
          <div>
            <Label htmlFor="pref-max" className="text-xs uppercase tracking-wider text-muted-foreground">Valor máx. (R$)</Label>
            <Input
              id="pref-max"
              data-testid="pref-max-price"
              type="number"
              min={0}
              value={prefs.max_price}
              onChange={(e) => setPrefs((p) => ({ ...p, max_price: e.target.value }))}
              placeholder="sem limite"
              className="h-11 mt-2"
            />
          </div>
        </section>

        {/* Keywords */}
        <section className="bg-card border border-border rounded-2xl p-5" data-testid="prefs-keywords">
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">Palavras-chave</Label>
          <p className="text-xs text-muted-foreground mt-1">
            Ex: {'"iphone"'}, {'"reforma"'}, {'"aula de inglês"'}. Aparece só se o pedido citar alguma delas.
          </p>
          <div className="flex gap-2 mt-2">
            <Input
              value={kwInput}
              onChange={(e) => setKwInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addKeyword())}
              placeholder="Digite e pressione Enter"
              data-testid="pref-kw-input"
              className="h-11"
            />
            <Button type="button" onClick={addKeyword} data-testid="pref-kw-add" variant="outline" className="h-11">
              Adicionar
            </Button>
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            {prefs.keywords.map((k) => (
              <span key={k} data-testid={`pref-kw-${k}`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-secondary text-sm">
                <Sparkles size={12} className="text-primary" />
                {k}
                <button type="button" onClick={() => removeItem("keywords", k)} className="hover:opacity-70">
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>
        </section>

        {/* Brands */}
        <section className="bg-card border border-border rounded-2xl p-5" data-testid="prefs-brands">
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">Marcas de interesse</Label>
          <p className="text-xs text-muted-foreground mt-1">Ex: Apple, Samsung, Volkswagen. Filtra pedidos que citam essas marcas.</p>
          <div className="flex gap-2 mt-2">
            <Input
              value={brandInput}
              onChange={(e) => setBrandInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addBrand())}
              placeholder="Digite e pressione Enter"
              data-testid="pref-brand-input"
              className="h-11"
            />
            <Button type="button" onClick={addBrand} data-testid="pref-brand-add" variant="outline" className="h-11">
              Adicionar
            </Button>
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            {prefs.brands.map((b) => (
              <span key={b} data-testid={`pref-brand-${b}`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-secondary text-sm">
                {b}
                <button type="button" onClick={() => removeItem("brands", b)} className="hover:opacity-70">
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>
        </section>

        {/* Conditions */}
        <section className="bg-card border border-border rounded-2xl p-5" data-testid="prefs-conditions">
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">Condição do item</Label>
          <p className="text-xs text-muted-foreground mt-1">Deixe vazio para receber todas.</p>
          <div className="grid grid-cols-3 gap-2 mt-3">
            {CONDITIONS.map((c) => {
              const active = prefs.conditions.includes(c.v);
              return (
                <button
                  key={c.v}
                  type="button"
                  data-testid={`pref-cond-${c.v}`}
                  onClick={() => toggleArr("conditions", c.v)}
                  className={`h-12 rounded-xl border text-sm font-bold transition-colors ${
                    active
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border hover:border-primary/50"
                  }`}
                >
                  {c.icon} {c.label}
                </button>
              );
            })}
          </div>
        </section>

        {/* Payment methods */}
        <section className="bg-card border border-border rounded-2xl p-5" data-testid="prefs-payments">
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">Formas de pagamento aceitas</Label>
          <p className="text-xs text-muted-foreground mt-1">Só para compras e serviços. Vazio = aceita todas.</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-3">
            {PAYMENT_METHODS.map((p) => {
              const active = prefs.payment_methods.includes(p.value);
              return (
                <button
                  key={p.value}
                  type="button"
                  data-testid={`pref-pay-${p.value}`}
                  onClick={() => toggleArr("payment_methods", p.value)}
                  className={`h-11 rounded-xl border text-sm font-medium transition-colors ${
                    active
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border hover:border-primary/50"
                  }`}
                >
                  {p.label}
                </button>
              );
            })}
          </div>
        </section>

        {/* Service locations */}
        <section className="bg-card border border-border rounded-2xl p-5" data-testid="prefs-svcloc">
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">Atendimento (para serviços)</Label>
          <p className="text-xs text-muted-foreground mt-1">Só influencia oportunidades de Serviços. Vazio = aceita todas.</p>
          <div className="grid grid-cols-3 gap-2 mt-3">
            {SERVICE_LOCATIONS.map((s) => {
              const active = prefs.service_locations.includes(s.v);
              return (
                <button
                  key={s.v}
                  type="button"
                  data-testid={`pref-svcloc-${s.v}`}
                  onClick={() => toggleArr("service_locations", s.v)}
                  className={`h-11 rounded-xl border text-sm font-medium transition-colors ${
                    active
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border hover:border-primary/50"
                  }`}
                >
                  {s.label}
                </button>
              );
            })}
          </div>
        </section>

        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => nav(-1)}
            data-testid="prefs-cancel"
            className="flex-1 h-12"
          >
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={save}
            disabled={saving}
            data-testid="prefs-save"
            className="flex-1 h-12 font-bold gap-2"
          >
            <Save size={16} />
            {saving ? "Salvando..." : "Salvar especialidades"}
          </Button>
        </div>
      </div>
    </div>
  );
}
